// What's in the plan, as the Map and Days draw it. A choice's options stay out until one is picked;
// the picked one borrows its choice's time and place when it has none of its own, so "Kintetsu,
// chosen" shows on the day of "Kyoto to Nara" without the AI copying it over.
import { daySpan } from "../../src/days.ts";
import { waitingOnRating } from "../../src/waits.ts";
import type { Item } from "../../src/web/types.ts";
import { point } from "./geo.ts";

const UNDECIDED = new Set(["option", "not_chosen"]);
// Picked: what makes a choice decided (chosen, booked, or done since).
const PICKED = new Set(["planned", "booked", "done"]);

export function inPlan(items: Item[]): Item[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  return items
    .filter((i) => i.type !== "choice" && !UNDECIDED.has(i.status))
    .map((i) => {
      const choice = i.parent ? byId.get(i.parent) : undefined;
      if (choice?.type !== "choice") return i;
      return {
        ...i,
        ...(i.when === undefined && choice.when !== undefined ? { when: choice.when } : {}),
        ...(i.where === undefined && i.from === undefined && choice.where !== undefined ? { where: choice.where } : {}),
        ...(i.from === undefined && choice.from !== undefined ? { from: choice.from } : {}),
        ...(i.to === undefined && choice.to !== undefined ? { to: choice.to } : {}),
      };
    });
}

// Choices, each with its options in order, by when they happen: a stay's first night, a move's day
// (the choice's own time, else its earliest option's), decided and undecided together. Undated ones
// come last, in the order they were added. `start`: the trip's first date, for "day 3".
export interface Choice {
  choice: Item;
  options: Item[];
  picked: Item | null;
}

export function choices(items: Item[], start: string | null = null): Choice[] {
  const firstDay = (i: Item) => daySpan(i.when, start)?.first ?? null;
  return items
    .filter((i) => i.type === "choice" && i.status !== "dropped")
    .map((choice) => {
      const options = items
        // A to-do about the choice ("Decide on Seoul") is under it too, but no option.
        .filter((i) => i.parent === choice.id && i.type !== "task" && i.status !== "dropped")
        .sort((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY));
      const picked = options.find((o) => PICKED.has(o.status)) ?? null;
      const days = [choice, ...options].map(firstDay).filter((d): d is number => d !== null);
      const day = firstDay(choice) ?? (days.length > 0 ? Math.min(...days) : Number.POSITIVE_INFINITY);
      return { choice, options, picked, day };
    })
    .sort((a, b) => a.day - b.day)
    .map(({ day: _, ...c }) => c);
}

// The choice an item is an option of (picked or not), or the choice itself; null for anything else.
export function choiceOf(item: Item, items: Item[]): Item | null {
  if (item.type === "choice") return item;
  const parent = item.parent ? items.find((i) => i.id === item.parent) : undefined;
  return parent?.type === "choice" ? parent : null;
}

// A choice's options still open (status option) that are a stay or a place with a place of their own are
// on the map all the time, as options (features/map.md), whether or not the choice already has one chosen
// or booked: a hotel weighed against the booked one is the point. Each, by id, with its choice. Journey
// options, and those ruled out (not chosen) or called off, show only when asked (Show on map).
export function openOptions(items: Item[]): Map<string, Item> {
  const out = new Map<string, Item>();
  for (const { choice, options } of choices(items))
    for (const o of options)
      if (o.status === "option" && (o.type === "stay" || o.type === "place") && point(o)) out.set(o.id, choice);
  return out;
}

// Whether it's one of a choice's options that hasn't been picked: off the map unless shown from Bookings.
export const isOpenOption = (item: Item) => UNDECIDED.has(item.status);

// Whether it's one of a choice's options shown as a tile (Options.tsx): any option not picked (still open,
// not chosen, or a booking called off), whose card in full opens as a modal on a computer (DESIGN.md,
// Bookings: "as a finding opens").
export function isOptionTile(item: Item, items: readonly Item[]): boolean {
  if (!item.parent || item.type === "choice" || item.type === "task" || PICKED.has(item.status)) return false;
  return items.some((i) => i.id === item.parent && i.type === "choice");
}

// What waits on my rating (src/waits.ts, the one rule), split by where it's rated and counted (DESIGN.md,
// Bookings, 2026-10-23): a choice's options still being compared (status option) under their choice's row,
// on Bookings' tab and its Waiting for you; everything else on Ideas, its tab, Waiting for you and the deck.
export function ratingWaits(items: readonly Item[], you: string): { ideas: Item[]; options: Item[] } {
  const choiceIds = new Set(items.filter((i) => i.type === "choice").map((i) => i.id));
  const ideas: Item[] = [];
  const options: Item[] = [];
  for (const i of waitingOnRating(items, you))
    (i.status === "option" && i.parent && choiceIds.has(i.parent) ? options : ideas).push(i);
  return { ideas, options };
}
